import { useTabaPayAddressUpdate } from 'features/moveMoney/screens/dcf/hooks/useTabaPayAddressUpdate';
import {
  checkForbiddenChars,
  getFlowNameForAnalytics,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import AddressValidation from 'features/signup/Address/AddressValidation';
import { SmartyStreetsResponseItem } from 'features/signup/helpers/smartyStreets';
import { getAddressLine } from 'features/signup/utils/signupUtils';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import VXLDropdown, {
  VXL_DROPDOWN_MENU_RESERVED_SPACE,
} from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LinkButton from 'shared/components/vxl/LinkButton/LinkButton';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS, isWeb } from 'shared/constants/platform';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import {
  findFullStateString,
  getStateAbbreviation,
  getStateString,
} from 'shared/utils/smartyStreet/smartyStreet';

interface TabapayAddressProps {
  screenType: string;
}

const TabaPayAddressScreen = ({ screenType }: TabapayAddressProps) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'address',
  });
  const isKeyboardVisible = useKeyboardVisibility();

  const [addressLine1Error, setAddressLine1Error] = useState(false);
  const [addressLine2Error, setAddressLine2Error] = useState(false);

  const {
    onSubmitEditingAction,
    height,
    showAddressField,
    updateSuggestedAddress,
    suggestions,
    setSelectedSearchTerms,
    setSearchTerms,
    loading,
    isDialogButtonLoading,
    setSelectedAddress,
    statesData,
    bottom,
    control,
    reset,
    showAddressFields,
    setIsOutsideClicked,
    errors,
    setShowAddressFields,
    onSubmit,
    isValid,
    bottomSheetRef,
    addressVerification,
    tabaPayEntryFlowType,
  } = useTabaPayAddressUpdate(screenType);

  return (
    <TouchableWithoutFeedback
      onPress={() => {
        setIsOutsideClicked(false);
      }}
      testID="touchable-area"
      accessible={false}
      disabled={isWeb}
    >
      <View style={styles.container} accessible={true}>
        <KeyboardAvoidingView
          style={styles.keyboardContainer}
          behavior={isIOS ? 'padding' : 'height'}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[
              !showAddressFields ? styles.scrollViewContainer : null,
              styles.addressDropdownBottomPadding,
            ]}
            nestedScrollEnabled={true}
          >
            <View style={styles.formContainer}>
              <TitleLgText numberOfLines={0}>
                {translatedText('title')}
              </TitleLgText>
              <BodyBaseText numberOfLines={0}>
                {translatedText('subTitle')}
              </BodyBaseText>
              <Controller
                control={control}
                name="addressLine1"
                render={({ field: { onChange, onBlur, value } }) => {
                  const onValueChange = (text: string) => {
                    setAddressLine1Error(checkForbiddenChars(text));
                    onChange(text);
                    setSearchTerms(text);
                  };

                  return (
                    <VXLSearchField
                      testID="text-input-textField.addressLine1"
                      labelText={translatedText('textField.addressLine1')}
                      options={(suggestions ?? []).map((item, index) => ({
                        key: `${item.address_line}-${index}`,
                        label: item.address_line,
                        value: item,
                      }))}
                      keepMenuOpenOnSelect={(item) => item.entries > 1}
                      onSelectOption={(item) => {
                        if (item.entries > 1) {
                          setSelectedSearchTerms(item);
                        } else {
                          setSelectedSearchTerms(
                            {} as SmartyStreetsResponseItem,
                          );
                          reset({
                            addressLine1: item?.street_line || '',
                            addressLine2: item?.secondary || '',
                            city: item?.city || '',
                            state: getStateAbbreviation(
                              statesData?.states ?? [],
                              item?.state ?? '',
                            ),
                            zip: item?.zipcode || '',
                          });
                          setShowAddressFields(true);
                          onChange(item?.street_line);
                          onBlur();
                          setSelectedAddress(getAddressLine(item));
                        }
                      }}
                      placeholder=""
                      value={value}
                      onBlur={() => {
                        setSelectedSearchTerms({} as SmartyStreetsResponseItem);
                        onBlur();
                      }}
                      onFocus={() => {
                        setIsOutsideClicked(false);
                      }}
                      onChangeText={onValueChange}
                      errorText={
                        addressLine1Error
                          ? translatedText('inlineError')
                          : (errors.addressLine1?.message ?? '')
                      }
                    />
                  );
                }}
              />
              {showAddressFields ? (
                <>
                  <Controller
                    control={control}
                    name="addressLine2"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <VXLTextInput
                        testID="text-input-textField.addressLine2"
                        labelText={translatedText('textField.addressLine2')}
                        errorText={
                          addressLine2Error
                            ? translatedText('inlineError')
                            : (errors.addressLine2?.message ?? '')
                        }
                        onChangeText={(text) => {
                          setAddressLine2Error(checkForbiddenChars(text));
                          onChange(text);
                          onBlur();
                        }}
                        onFocus={() => {
                          setIsOutsideClicked(false);
                        }}
                        value={value}
                        autoCapitalize="none"
                        onSubmitEditing={onSubmitEditingAction}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="city"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <VXLTextInput
                        testID="text-input-textField.city"
                        labelText={translatedText('textField.city')}
                        errorText={errors.city?.message}
                        onChangeText={(text) => {
                          onChange(text);
                          onBlur();
                        }}
                        onFocus={() => {
                          setIsOutsideClicked(false);
                        }}
                        value={value}
                        autoCapitalize="none"
                        onSubmitEditing={onSubmitEditingAction}
                      />
                    )}
                  />
                  {statesData?.states?.length ? (
                    <Controller
                      control={control}
                      name="state"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <VXLDropdown
                          testID="textField.state-select"
                          labelText={translatedText('textField.state')}
                          placeholder={translatedText('textField.state')}
                          options={statesData.states.map(getStateString)}
                          value={
                            value
                              ? (findFullStateString(
                                  statesData.states,
                                  value,
                                ) ?? '')
                              : ''
                          }
                          errorText={errors.state?.message}
                          onChangeText={(text) => {
                            onChange(
                              getStateAbbreviation(statesData.states, text),
                            );
                            onBlur();
                            setIsOutsideClicked(false);
                          }}
                        />
                      )}
                    />
                  ) : null}
                  <Controller
                    control={control}
                    name="zip"
                    render={({ field: { onChange, onBlur, value } }) => {
                      const onChangeText = (text: string) => {
                        onChange(text);
                        onBlur();
                      };
                      return (
                        <VXLTextInput
                          testID="text-input-textField.zip"
                          labelText={translatedText('textField.zip')}
                          errorText={errors.zip?.message}
                          onChangeText={onChangeText}
                          onFocus={() => {
                            setIsOutsideClicked(false);
                          }}
                          returnKeyType="done"
                          keyboardType="number-pad"
                          value={value}
                          autoCapitalize="none"
                          onSubmitEditing={onSubmitEditingAction}
                        />
                      );
                    }}
                  />
                </>
              ) : null}
              {!showAddressFields ? (
                <View style={styles.enterManuallyContainer}>
                  <LinkButton
                    linkOnPress={() => {
                      showAddressField();
                    }}
                    size="base"
                    text={translatedText('enterAddressManually')}
                    linkOnDark={false}
                  />
                </View>
              ) : null}
            </View>
          </ScrollView>
          <View
            style={[
              styles.buttonContainer,
              {
                paddingBottom: isKeyboardVisible
                  ? theme.spacing.xl
                  : bottom + theme.spacing.m,
              },
            ]}
          >
            <Button
              label={translatedText('buttons.next')}
              onPress={onSubmit}
              isLoading={loading}
              isDisabled={!isValid}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
              analyticsId="homeAddressSaved"
              customAttributes={{
                flow_name: getFlowNameForAnalytics(tabaPayEntryFlowType),
              }}
            />
          </View>
        </KeyboardAvoidingView>
        <BottomSheet ref={bottomSheetRef}>
          <AddressValidation
            handleNext={updateSuggestedAddress}
            fullAddress={addressVerification?.fullAddress}
            addressType={addressVerification?.addressType}
            closeBottomSheet={() => bottomSheetRef.current?.close()}
            isLoading={isDialogButtonLoading}
          ></AddressValidation>
        </BottomSheet>
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  addressDropdownBottomPadding: {
    paddingBottom: VXL_DROPDOWN_MENU_RESERVED_SPACE,
  },
  buttonContainer: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: `100%`,
  },
  enterManuallyContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing.m,
  },

  formContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  keyboardContainer: {
    flex: 1,
  },
  scrollViewContainer: {
    flex: 1,
    minHeight: 550,
  },
});

export default TabaPayAddressScreen;
